import { useEffect, useCallback, useRef, useState } from 'react';
import { useChatStore, type Conversation, type Message } from '../../stores/whatsapp/chatStore';
import waApi from '../../services/whatsapp/waApi';

// ─── API Integration ────────────────────────────────────────────────

async function fetchConversations(): Promise<Conversation[]> {
  const res = await waApi.get('/conversations');
  const payload = res.data?.items ?? res.data?.data ?? res.data;
  const data = Array.isArray(payload) ? payload : [];
  return data.map((c: any) => ({
    id: c._id || c.id, // eslint-disable-line no-underscore-dangle
    client_id: c.client_id,
    client_name: c.client_name,
    client_phone: c.client_phone,
    client_company: c.client_company,
    client_priority: c.client_priority || 'normal',
    assigned_agent_id: c.assigned_agent_id,
    assigned_agent_name: c.assigned_agent_name,
    department: c.department,
    assigned_department: c.assigned_department,
    status: c.status || 'open',
    priority: c.priority || 'medium',
    subject: c.subject,
    last_message: c.last_message,
    last_message_at: c.last_message_at,
    unread_count: c.unread_count || 0,
    started_at: c.started_at || c.created_at,
    rating: c.rating,
    rating_feedback: c.rating_feedback,
    ticket_protocol: c.ticket_protocol,
    skip_rating: c.skip_rating,
    fixed_department: c.fixed_department,
    wa_number_id: c.wa_number_id,
    wa_number_label: c.wa_number_label,
    wa_number_phone: c.wa_number_phone,
    wa_number_phone_id: c.wa_number_phone_id,
  }));
}

async function fetchMessages(conversationId: string): Promise<Message[]> {
  const res = await waApi.get(`/conversations/${conversationId}/messages`);
  const payload = res.data?.items ?? res.data?.data ?? res.data;
  const data = Array.isArray(payload) ? payload : [];
  return data.map((m: any) => ({
    id: m._id || m.id, // eslint-disable-line no-underscore-dangle
    conversation_id: m.conversation_id,
    sender_type: m.sender_type,
    sender_id: m.sender_id,
    content: m.content,
    message_type: m.message_type || 'text',
    status: m.status || 'sent',
    is_internal_note: m.is_internal_note || false,
    created_at: m.created_at,
    media_url: m.media_url,
    media_filename: m.media_filename,
    media_mime_type: m.media_mime_type,
    media_size: m.media_size,
  }));
}

/**
 * Rebusca conversas + mensagens da conversa ativa e popula o store.
 *
 * Usado no "catch-up" da reconexão do WebSocket: enquanto o WS está caído,
 * mensagens novas (do cliente e do bot) não chegam por evento; ao reconectar,
 * isso recupera o que foi perdido sem o usuário precisar dar F5. Independente
 * de hooks (lê/escreve o store direto), para poder ser chamado do onOpen do WS.
 */
export async function refetchAllChatData(): Promise<void> {
  const store = useChatStore.getState();
  try {
    const conversas = await fetchConversations();
    store.setConversations(conversas);
  } catch {
    // mantém o estado atual se a busca falhar
  }
  const activeId = useChatStore.getState().activeConversationId;
  if (activeId) {
    try {
      const msgs = await fetchMessages(activeId);
      useChatStore.getState().setMessages(activeId, msgs);
    } catch {
      // idem
    }
  }
}

// ─── Hooks ──────────────────────────────────────────────────────────

export function useConversations() {
  const conversations = useChatStore(s => s.conversations);
  const setConversations = useChatStore(s => s.setConversations);
  const [isLoading, setIsLoading] = useState(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    setIsLoading(true);
    fetchConversations()
      .then(data => setConversations(data))
      .catch(() => setConversations([]))
      .finally(() => setIsLoading(false));
  }, [setConversations]);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchConversations();
      setConversations(data);
      return true;
    } catch {
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [setConversations]);

  return { data: conversations, isLoading, refresh };
}

// Busca conversas para a tela de Historico, incluindo as de departamentos nao
// permitidos — que vem com os campos sensiveis JA mascarados pelo servidor
// (redacted=true). Usa estado LOCAL (nao o chatStore global) para nao misturar
// conversas restritas no chat ao vivo.
async function fetchHistoryConversations(): Promise<Conversation[]> {
  const res = await waApi.get('/conversations', { params: { include_restricted: true } });
  const payload = res.data?.items ?? res.data?.data ?? res.data;
  const data = Array.isArray(payload) ? payload : [];
  return data.map((c: any) => ({
    id: c._id || c.id, // eslint-disable-line no-underscore-dangle
    client_id: c.client_id,
    client_name: c.client_name,
    client_phone: c.client_phone,
    client_company: c.client_company,
    client_priority: c.client_priority || 'normal',
    assigned_agent_id: c.assigned_agent_id,
    assigned_agent_name: c.assigned_agent_name,
    department: c.department,
    assigned_department: c.assigned_department,
    status: c.status || 'open',
    priority: c.priority || 'medium',
    subject: c.subject,
    last_message: c.last_message,
    last_message_at: c.last_message_at,
    unread_count: c.unread_count || 0,
    started_at: c.started_at || c.created_at,
    rating: c.rating,
    ticket_protocol: c.ticket_protocol,
    wa_number_label: c.wa_number_label,
    redacted: c.redacted ?? false,
  }));
}

export function useHistoryConversations() {
  const [data, setData] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    setIsLoading(true);
    fetchHistoryConversations()
      .then(setData)
      .catch(() => setData([]))
      .finally(() => setIsLoading(false));
  }, []);

  return { data, isLoading };
}

export function useMessages(conversationId: string | null) {
  const messages = useChatStore(s => (conversationId ? s.messages[conversationId] : undefined));
  const setMessages = useChatStore(s => s.setMessages);
  const [isLoading, setIsLoading] = useState(false);
  const prevIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!conversationId || conversationId === prevIdRef.current) return;
    prevIdRef.current = conversationId;

    setIsLoading(true);
    fetchMessages(conversationId)
      .then(data => setMessages(conversationId, data))
      .catch(() => setMessages(conversationId, []))
      .finally(() => setIsLoading(false));
  }, [conversationId, setMessages]);

  return { data: messages ?? [], isLoading };
}
